import { useState, useCallback, useMemo, useEffect, memo } from "react";
import { useLoaderData } from "react-router-dom";
import ListGroup from "react-bootstrap/ListGroup";
import { CadModel } from "./api/Application";
import { Api } from "./api/Api";
import { CadModelLoaderData } from "./loaders/cadModelListLoader";

const VIEWS = ["top", "side", "front", "rear"] as const;

type View = (typeof VIEWS)[number];

const VEHICLE_TYPE_KEYS: Record<string, string> = {
    q: "Car",
    w: "SUV",
    e: "Van",
    r: "Pickup",
    a: "Freight",
    s: "Bus",
    z: "Convertible",
    x: "Hatchback",
};

// ── Sprite Sheet ───────────────────────────────────────────────────

interface SpriteTile {
    col: number;
    x:   number;
    y:   number;
}

interface SpriteEntry {
    row:   number;
    tiles: Record<View, SpriteTile>;
}

interface SpriteIndex {
    tileSize: number;
    cols:     number;
    rows:     number;
    views:    View[];
    count:    number;
    index:    Record<string, SpriteEntry>;
}

export interface SpriteSheet {
    img:  HTMLImageElement;
    meta: SpriteIndex;
}

export async function loadSpriteSheet(
    spriteUrl: string,
    indexUrl: string,
): Promise<SpriteSheet> {
    const [meta, img] = await Promise.all( [
        fetch( indexUrl ).then( r => r.json() ) as Promise<SpriteIndex>,
        new Promise<HTMLImageElement>( ( resolve, reject ) => {
            const i = new Image();
            i.onload  = () => resolve( i );
            i.onerror = reject;
            i.src     = spriteUrl;
        } ),
    ] );

    // Re-key index from padded strings to ints to match DB IDs
    const rekeyed: Record<string, SpriteEntry> = {};
    for( const [id, entry] of Object.entries( meta.index ) ) {
        rekeyed[String( Number( id ) )] = entry;
    }
    meta.index = rekeyed;

    return { img, meta };
}

// ── Thumbnail extraction ───────────────────────────────────────────

/** Extract a single view tile for one collection as a data-URL. */
function extractTile(
    sprite: SpriteSheet,
    entry:  SpriteEntry,
    view:   View,
): string {
    const ts = sprite.meta.tileSize;
    const tile = entry.tiles[view];
    const c = document.createElement( "canvas" );
    c.width  = ts;
    c.height = ts;
    const ctx = c.getContext( "2d" )!;
    ctx.drawImage( sprite.img, tile.x, tile.y, ts, ts, 0, 0, ts, ts );
    return c.toDataURL( "image/png" );
}

/** Extract all view thumbnails for every collection. */
export function extractAllThumbnails( sprite: SpriteSheet ): Map<number, Record<View, string>> {
    const thumbs = new Map<number, Record<View, string>>();
    for( const [id, entry] of Object.entries( sprite.meta.index ) ) {
        const views = {} as Record<View, string>;
        for( const view of VIEWS ) {
            views[view] = extractTile( sprite, entry, view );
        }
        thumbs.set( Number( id ), views );
    }
    return thumbs;
}

// ── Components ─────────────────────────────────────────────────────

interface CadModelItemProps {
    model:     CadModel;
    selected:  boolean;
    thumbnail: Record<View, string> | undefined;
    onClick:   ( m: CadModel ) => void;
    onHover:   ( m: CadModel | undefined ) => void;
}

function CadModelItem( { model, selected, thumbnail, onClick, onHover }: CadModelItemProps ) {
    return (
        <ListGroup.Item
            active={selected}
            action
            className="d-flex align-items-center gap-2 py-1 px-2"
            onClick={() => onClick( model )}
            onMouseEnter={() => onHover( model )}
            onMouseLeave={() => onHover( undefined )}
        >
            {thumbnail && <img
                src={thumbnail.top}
                alt={model.modelName ?? model.modelId}
                style={{ width: 48, height: 48, objectFit: "contain", background: "#fff", borderRadius: 4 }}
            />}
            <div className="d-flex flex-column overflow-hidden">
                <span className="text-truncate fw-semibold small">
                    {model.modelName ?? model.modelId}
                </span>
                <span className="text-truncate text-muted small">
                    {[model.carMake, model.carModel, model.carYear].filter( Boolean ).join( " " )}
                </span>
                <span className="text-truncate text-muted small">
                    {model.attributes?.vehicleType ?? "—"}
                </span>
            </div>
        </ListGroup.Item>
    );
}

const CadModelItemMemo = memo( CadModelItem );

interface VehicleDetailProps {
    model:      CadModel;
    thumbnails: Map<number, Record<View, string>>;
}

function VehicleDetail( { model, thumbnails }: VehicleDetailProps ) {
    const views = thumbnails.get( model.id );

    return (
        <div className="d-flex w-100 h-100 align-items-center">
            {VIEWS.map( view => (
                <img
                    key={view}
                    src={views?.[view] ?? ""}
                    alt={`${model.id} ${view}`}
                    style={{
                        width:          "25%",
                        height:         "auto",
                        objectFit:      "contain",
                        imageRendering: "crisp-edges"
                    }}
                />
            ) )}
        </div>
    );
}

// ── Page ───────────────────────────────────────────────────────────

export function CadModelAnnotationPage() {
    const { models: initialModels, thumbnails } = useLoaderData() as CadModelLoaderData;
    const [models,    setModels]    = useState( initialModels );
    const [selModel,  setSelModel]  = useState<CadModel | undefined>();
    const [hovModel,  setHovModel]  = useState<CadModel | undefined>();
    const [search,    setSearch]    = useState( "" );

    const filtered = useMemo( () => {
        if( !search.trim() ) return models;
        const q = search.toLowerCase();
        return models.filter( m =>
            ( m.modelName  ?? "" ).toLowerCase().includes( q ) ||
            ( m.carMake    ?? "" ).toLowerCase().includes( q ) ||
            ( m.carModel   ?? "" ).toLowerCase().includes( q ) ||
            ( m.modelId           ).toLowerCase().includes( q )
        );
    }, [models, search] );

    const handleSelect = useCallback( ( m: CadModel ) => {
        setSelModel( prev => prev?.id === m.id ? undefined : m );
    }, [] );

    const items = useMemo( () => {
        return filtered.map( m => (
            <CadModelItemMemo
                key={m.id}
                model={m}
                selected={selModel?.id === m.id}
                thumbnail={thumbnails.get( m.id )}
                onClick={handleSelect}
                onHover={setHovModel}
            />
        ) );
    }, [filtered, selModel, handleSelect, thumbnails] );

    // Arrow key navigation + vehicle type assignment
    useEffect( () => {
        function onKeyDown( ev: KeyboardEvent ) {
            if( ev.target instanceof HTMLInputElement ) return;
            if( ev.key === "ArrowDown" || ev.key === "ArrowUp" ) {
                ev.preventDefault();
                const list = filtered;
                if( !list.length ) return;
                if( !selModel ) {
                    setSelModel( list[0] );
                    return;
                }
                const idx = list.findIndex( ( m: CadModel ) => m.id === selModel.id );
                const next = ev.key === "ArrowDown"
                    ? Math.min( idx + 1, list.length - 1 )
                    : Math.max( idx - 1, 0 );
                setSelModel( list[next] );
                return;
            }

            const vehicleType = VEHICLE_TYPE_KEYS[ev.key];
            if( vehicleType && selModel ) {
                ev.preventDefault();
                Api.cadModelUpdateAttributes( selModel.id!, { attributes: { vehicleType } } ).then( res => {
                    if( !res.isOk ) return;
                    const updated = res.value;
                    setModels( prev => prev.map( m => m.id === updated.id ? res.value : m ) );
                    const idx = filtered.findIndex( ( m: CadModel ) => m.id === selModel.id );
                    const next = Math.min( idx + 1, filtered.length - 1 );
                    setSelModel( filtered[next] );
                } );
            }
        }
        window.addEventListener( "keydown", onKeyDown );
        return () => window.removeEventListener( "keydown", onKeyDown );
    }, [filtered, selModel] );

    return (
        <div id="cad-model-page" className="d-flex" style={{ height: "100vh" }}>
            <div style={{ width: 400 }} className="border-end d-flex flex-column">
                <div className="bg-body-tertiary d-flex flex-row w-100 justify-content-center align-items-center gap-2 p-2 border-bottom border-2 border-body-tertiary">
                    <div className="flex-grow-1 ps-2 fs-5">{filtered.length} Models</div>
                </div>
                <div className="px-2 py-2">
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search models..."
                        value={search}
                        onChange={e => setSearch( e.target.value )}
                    />
                </div>
                <ListGroup variant="flush" className="y-scroll-auto flex-grow-1" style={{ overflowY: "auto" }}>
                    {items}
                </ListGroup>
            </div>
            <div className="flex-grow-1 d-flex align-items-center justify-content-center"
                 style={{ position: "relative", backgroundColor: "#fff", overflowY: "auto" }}>
                {selModel
                    ? <VehicleDetail model={selModel} thumbnails={thumbnails} />
                    : <span className="text-muted">Select a model</span>
                }
            </div>
        </div>
    );
}